import { clsx } from 'clsx';
import { useReducer, useRef } from 'react';
import { HiddenSelect, useSelect } from 'react-aria';
import { Item, useSelectState } from 'react-stately';
import { isNil } from 'rambdax';

import CaretDownIcon from '@ping/assets/Icon/caret-down.svg';
import { useToggle } from '@ping/hooks';
import { FieldGroup, ListBox, Loading, PrimitivePopover } from '@ping/uikit';

import style from './style.module.scss';

import type IReactStately from 'react-stately';
import type { Selection, Key } from '@react-types/shared';
import { type PopoverPlacement } from '../PrimitivePopover/PrimitivePopoverRoot';

const BORDER_WIDTH = 1 as const;

export interface ISelectProps<T> extends IReactStately.SelectProps<T>, ICustomizable {
  emptyState?: React.ReactNode;
  name?: string | undefined;
  topElement?: React.ReactNode;
  magnet?: IMagnet;
  boundaryRef?: React.MutableRefObject<HTMLElement>;
  buttonContent?: React.ReactNode;
  isLoading?: boolean;
  wrapperClassName?: string;
  onPanelPlacementChange?: (placement: PopoverPlacement) => void;
  // Optional multiple-selection mode (default is single)
  isMultiple?: true;
  multiSelectedKeys?: Selection;
  multiDefaultSelectedKeys?: Selection;
  onMultiSelectionChange?: (keys: Selection) => void;
}

// TODO: break this component into multiple sub-components, styling and handling more logic is not clean.
export const Select = <T extends object>(props: ISelectProps<T>) => {
  //
  // Create state based on the incoming props
  const state = useSelectState(props);
  const [isPopoverTriggerFocused, togglePopoverTriggerFocusState] = useToggle(false);
  //
  // Get props for child elements from useSelect
  const boundaryRef = useRef<HTMLDivElement>();
  const triggerRef = useRef();
  const { labelProps, triggerProps, valueProps, menuProps } = useSelect(props, state, triggerRef);
  //
  // Set state based on child popover element
  const [placement, emitPlacementChange] = useReducer<
    (prev: PopoverPlacement, next: PopoverPlacement) => PopoverPlacement
  >((prev, next) => {
    if (prev !== next) {
      props.onPanelPlacementChange?.(next);
    }
    return next;
  }, 'bottom');

  // Compute button value for single or multiple selection
  const getButtonValue = () => {
    if (props.buttonContent) {
      return props.buttonContent;
    }

    if (!props.isMultiple) {
      return state.selectedItem ? state.selectedItem.rendered : props.placeholder;
    }

    const selected = props.multiSelectedKeys;
    if (!selected) {
      return props.placeholder;
    }
    if (selected === 'all') {
      return 'All selected';
    }
    const size = (selected as Set<React.Key>).size ?? 0;
    if (!size) {
      return props.placeholder;
    }
    return `${size} selected`;
  };

  const getSelectedKeysForHiddenInputs = (): string[] => {
    if (!props.isMultiple || !props.name) return [];
    const items = (props.items as any[]) ?? [];
    const selected = props.multiSelectedKeys;
    if (!selected) return [];
    if (selected === 'all') {
      return items.map(it => String(it?.id));
    }
    return Array.from(selected as Set<Key>).map(k => String(k));
  };

  return (
    <FieldGroup className={clsx(style['select'], props.wrapperClassName)}>
      {!isNil(props.label) && (
        <FieldGroup.Label {...labelProps} className={style['select__label']}>
          {props.label}
        </FieldGroup.Label>
      )}

      <FieldGroup.Control
        className={style['select__control']}
        magnet={props.magnet}
        ref={isNil(props.boundaryRef) ? boundaryRef : undefined}
        data-part='select-control'
        data-state={state.isOpen ? 'open' : 'closed'}
        data-placement={placement}
      >
        {/* Hidden inputs for multi-select to submit values with HTML forms */}
        {props.isMultiple &&
          props.name &&
          getSelectedKeysForHiddenInputs().map(key => (
            <input key={`hidden-${key}`} type='hidden' name={props.name} value={key} />
          ))}

        {/* HiddenSelect only for single-select; multi handled via hidden inputs above */}

        {/* Hidden inputs for multi-select to submit values with HTML forms */}
        {!props.isMultiple && (
          <HiddenSelect
            state={state}
            triggerRef={triggerRef}
            label={props.label}
            name={props.isMultiple ? undefined : props.name}
          />
        )}

        <PrimitivePopover.Root
          state={state}
          triggerRef={triggerRef}
          boundaryRef={props.boundaryRef ?? boundaryRef}
          offset={BORDER_WIDTH}
          onFlipped={emitPlacementChange}
        >
          <PrimitivePopover.Trigger
            {...triggerProps}
            className={style['select__trigger']}
            data-magnet={props.magnet}
            onFocusChange={togglePopoverTriggerFocusState}
          >
            <span {...valueProps}>{getButtonValue()}</span>

            <Loading className={style['select__loading']} data-hidden={!props.isLoading} aria-hidden='true' />
            <CaretDownIcon
              className={style['select__indicator']}
              data-hidden={props.isLoading}
              data-open={state.isOpen}
              aria-hidden='true'
            />
          </PrimitivePopover.Trigger>

          {!props.isLoading && !state.collection.size && !isPopoverTriggerFocused && (
            <p className={style['select__empty-state']}>{props.emptyState}</p>
          )}

          <PrimitivePopover.Portal>
            <PrimitivePopover.Panel
              className={clsx(style['select__panel'], props.className)}
              data-part='popover-content'
            >
              {props.topElement}
              <ListBox
                {...menuProps}
                className={style['select__list']}
                data-placement={placement}
                {...(props.isMultiple
                  ? {
                      items: props.items,
                      children: props.children,
                      selectionMode: 'multiple' as const,
                      selectedKeys: props.multiSelectedKeys,
                      defaultSelectedKeys: props.multiDefaultSelectedKeys,
                      onSelectionChange: props.onMultiSelectionChange,
                    }
                  : {
                      children: undefined,
                      state,
                    })}
              />
            </PrimitivePopover.Panel>
          </PrimitivePopover.Portal>
        </PrimitivePopover.Root>
      </FieldGroup.Control>
    </FieldGroup>
  );
};

interface ISelectItemProps {
  <T>(props: IReactStately.ItemProps<T> & ICustomizable): JSX.Element;
}

Select.Item = Item as ISelectItemProps;
